import {Component, OnInit} from '@angular/core';
import {Actions, ofActionErrored, ofActionSuccessful, Select, Store} from '@ngxs/store';
import {VehicleState} from '@shared/state/vehicle/vehicle.state';
import {Observable, Subject} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastService} from '@app/service/util/toast.service';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {GetMakes, GetModelsData} from '@shared/state/vehicle/vehicle.actions';
import {take, takeUntil} from 'rxjs/operators';
import {CreateDataset, SimulatePrice} from '@shared/state/ml/ml.actions';

@Component({
  selector: 'app-create-dataset',
  templateUrl: './create-dataset.component.html',
  styleUrls: ['./create-dataset.component.scss']
})
export class CreateDatasetComponent implements OnInit {
  @Select(VehicleState.getMakes) vehicleMakes$: Observable<any[]>;
  @Select(VehicleState.getModels) vehicleModels$: Observable<any>;
  submitted: boolean;

  datasetForm: FormGroup;

  makeList: any[];
  modelList: any[];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder, private store: Store, private toastService: ToastService,
              private actions: Actions, private modalRef: NzModalRef, private modal: NzModalService) {
  }

  ngOnInit() {
    this.submitted = false;

    this.datasetForm = this.fb.group({
      carMake: ['', Validators.required],
      carModel: ['', Validators.required],
      isMultipleCarModels: [false, Validators.required],
      shouldRefreshSegments: [false, Validators.required],
      shouldRefreshListings: [false, Validators.required]
    });

    this.store.dispatch(new GetMakes());
    this.setupVehicleData();
  }

  get f() {
    return this.datasetForm.controls;
  }

  resetForms() {
    this.datasetForm.reset();
  }

  setupVehicleData() {
    // This will only be triggered when the first call to GetModels happens in the onMakeChange function
    this.actions
      .pipe(
        ofActionSuccessful(GetModelsData),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        let makeValue = this.f.carMake.value;
        // If a make is already assigned, and it has models, assign to the model list
        if (makeValue) {
          this.modelList = this.store.selectSnapshot(VehicleState.getModels)[makeValue];
        }
      });
  }

  onIsMultipleModelsChange(value) {
    if (!value) {
      this.f['carModel'].setValidators([Validators.required]);
    } else {
      this.f['carModel'].reset();
      this.f['carModel'].clearValidators();
    }
    this.f['carModel'].updateValueAndValidity();
  }

  onCarMakeChange(value: string) {
    if (!value) {
      return;
    }
    this.store.dispatch(new GetModelsData({make: value}));

    this.f.carModel.reset(); // Changing the model should remove previous model data
  }

  createDataset(): any {
    this.submitted = true;

    // stop here if form is invalid
    if (this.datasetForm.invalid) {
      return;
    }

    const data = this.datasetForm.value;
    this.store.dispatch(new CreateDataset(data));

    this.actions
      .pipe(
        ofActionSuccessful(CreateDataset),
        take(1)
      )
      .subscribe(() => {
        this.submitted = false;
        this.resetForms();
        this.modalRef.close();
      });
    this.actions
      .pipe(
        ofActionErrored(CreateDataset),
        take(1)
      )
      .subscribe(() => {
        this.submitted = false;
      });
  }

  showCreateDatasetConfirm(): void {
    this.modal.confirm({
      nzTitle: '<i>Verify the contents of this create dataset request</i>',
      nzContent: `
         <b>Make:</b> ${this.f.carMake.value}<br>
         <b>Model:</b> ${this.f.carModel.value || 'Multiple Models'}<br>
         <b>Refresh Segments:</b> ${this.f.shouldRefreshSegments.value}<br>
         <b>Refresh Listings:</b> ${this.f.shouldRefreshListings.value}<br>
      `,
      nzOkText: 'Create Dataset',
      nzOkType: 'primary',
      nzOnOk: () => this.createDataset(),
      nzCancelText: 'Cancel'
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
